import { useEffect } from 'react';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { useT } from '../lib/i18n';
import { hapticSelection } from '../lib/telegram';

/**
 * Routes where the bottom nav is shown. Detail pages and wizard flows are
 * intentionally excluded so the user has full focus.
 */
const SHOW_ON: ReadonlyArray<RegExp> = [
  /^\/$/,
  /^\/my\/rides$/,
  /^\/my\/bookings$/,
  /^\/profile$/,
];

interface Item {
  to: string;
  labelKey: 'nav.search' | 'nav.bookings' | 'nav.rides' | 'nav.profile';
  iconPath: string;
}

const ITEMS: Item[] = [
  {
    to: '/',
    labelKey: 'nav.search',
    iconPath: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  },
  {
    to: '/my/bookings',
    labelKey: 'nav.bookings',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  },
  {
    to: '/my/rides',
    labelKey: 'nav.rides',
    // Side-view car (car-side.svg, SVG Repo). Single path, 24×24, stroke style.
    iconPath:
      'M8 17H16M8 17C8 18.1046 7.10457 19 6 19C4.89543 19 4 18.1046 4 17M8 17C8 15.8954 7.10457 15 6 15C4.89543 15 4 15.8954 4 17M16 17C16 18.1046 16.8954 19 18 19C19.1046 19 20 18.1046 20 17M16 17C16 15.8954 16.8954 15 18 15C19.1046 15 20 15.8954 20 17M10 5V11M4 11L4.33152 9.01088C4.56901 7.58593 4.68776 6.87345 5.0433 6.3388C5.35671 5.8675 5.79705 5.49447 6.31346 5.26281C6.8993 5 7.6216 5 9.06621 5H12.4311C13.3703 5 13.8399 5 14.2662 5.12945C14.6436 5.24406 14.9946 5.43194 15.2993 5.68236C15.6435 5.96523 15.904 6.35597 16.425 7.13744L19 11M4 17H3.6C3.03995 17 2.75992 17 2.54601 16.891C2.35785 16.7951 2.20487 16.6422 2.10899 16.454C2 16.2401 2 15.9601 2 15.4V14.2C2 13.0799 2 12.5198 2.21799 12.092C2.40973 11.7157 2.71569 11.4097 3.09202 11.218C3.51984 11 4.0799 11 5.2 11H17.2C17.9432 11 18.3148 11 18.6257 11.0492C20.3373 11.3203 21.6797 12.6627 21.9508 14.3743C22 14.6852 22 15.0568 22 15.8C22 15.9858 22 16.0787 21.9877 16.1564C21.9199 16.5843 21.5843 16.9199 21.1564 16.9877C21.0787 17 20.9858 17 20.8 17H20',
  },
  {
    to: '/profile',
    labelKey: 'nav.profile',
    iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  },
];

export function BottomNav(): JSX.Element | null {
  const { path, navigate } = useRouter();
  const { me } = useSession();
  const { t } = useT();
  const visible = SHOW_ON.some((re) => re.test(path));
  // Role-gate: the Rides tab is driver-only. Passengers don't see it (and won't
  // land on an empty driver page they can't use).
  const items = ITEMS.filter((it) => it.to !== '/my/rides' || me?.isDriver);

  useEffect(() => {
    if (visible) document.body.classList.add('has-bottom-nav');
    else document.body.classList.remove('has-bottom-nav');
    return () => document.body.classList.remove('has-bottom-nav');
  }, [visible]);

  if (!visible) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--border-light)] bg-tg-bg-secondary pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      <ul className="mx-auto flex max-w-md justify-around">
        {items.map((it) => {
          const active = path === it.to;
          return (
            <li key={it.to} className="flex-1">
              <button
                type="button"
                onClick={() => {
                  if (!active) hapticSelection();
                  navigate(it.to);
                }}
                aria-current={active ? 'page' : undefined}
                className={
                  'flex w-full flex-col items-center gap-0.5 px-2 py-2 text-xs transition ' +
                  // Inactive uses the theme-aware hint colour. The old
                  // hover:text-brand-navy was invisible on dark Telegram themes
                  // and "stuck" after a tap on touch devices.
                  (active ? 'text-brand-green' : 'text-tg-hint')
                }
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d={it.iconPath} />
                </svg>
                <span className="font-medium">{t(it.labelKey)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
